import { faEye, faEyeSlash } from '@fortawesome/pro-solid-svg-icons';
import type { ReactNode } from 'react';
import styled from 'styled-components';

import type { Currency } from '__generated__/globalTypes';
import { IconButton } from 'atoms/buttons/IconButton';
import { Vertical } from 'atoms/layout/flex';
import { BodyM, BodyS } from 'atoms/typography';
import { Dots } from 'atoms/ui/Dots';
import { useAmountWithConversion } from 'hooks/useAmountWithConversion';
import { useCryptoBalance } from 'hooks/useCryptoBalance';
import { useToggleHideBalance } from 'hooks/useToggleHideBalance';
import type { MonetaryAmount } from 'lib/monetaryAmount';

const Container = styled.div<{ $inline: boolean }>`
  display: flex;
  align-items: center;

  ${props =>
    props.$inline ? 'gap: var(--unit);' : `justify-content: space-between`};
`;

const Amount = styled.div`
  text-align: center;
  text-align: left;
`;

const Actions = styled.div`
  display: flex;
  gap: var(--unit);
`;

const Icon = styled(IconButton)<{ $inline: boolean }>`
  margin-bottom: var(--double-unit);
  ${props => (props.$inline ? `margin-bottom: 0;` : '')}
`;

const Line = styled.div`
  display: flex;
  align-items: baseline;
  gap: var(--half-unit);
`;

const MainXL = styled.span`
  font: var(--t-bold) var(--t-32);
`;

const Exponent = styled(BodyS)`
  color: var(--c-nd-600);
`;

const ExponentXL = styled(BodyM)`
  color: var(--c-nd-600);
`;

type UserBalanceAmountProps = {
  inline?: boolean;
  hideBalance?: boolean;
  monetaryBalance: MonetaryAmount;
  primaryCurrency?: Currency;
};

const UserBalanceAmount = ({
  inline,
  hideBalance,
  monetaryBalance,
  primaryCurrency,
}: UserBalanceAmountProps) => {
  const { main, exponent } = useAmountWithConversion({
    monetaryAmount: monetaryBalance,
    primaryCurrency,
  });

  if (inline)
    return (
      <Line>
        {!!main && (
          <BodyS color="var(--c-white)">
            {hideBalance ? <Dots size="small" count={7} /> : main}
          </BodyS>
        )}
        {!!exponent && (
          <Exponent>
            {hideBalance ? <Dots size="small" count={7} /> : `≈ ${exponent}`}
          </Exponent>
        )}
      </Line>
    );
  return (
    <Vertical gap={0.5}>
      {!!main && (
        <MainXL>{hideBalance ? <Dots size="medium" count={7} /> : main}</MainXL>
      )}
      {!!exponent && (
        <ExponentXL>
          {hideBalance ? <Dots size="small" count={7} /> : `≈ ${exponent}`}
        </ExponentXL>
      )}
    </Vertical>
  );
};

type Props = {
  inline?: boolean;
  right?: ReactNode;
  disableToggle?: boolean;
  primaryCurrency?: Currency;
};

export const UserBalance = ({
  inline,
  right,
  disableToggle = false,
  primaryCurrency,
}: Props) => {
  const { toggleHideBalance, loading } = useToggleHideBalance();
  const { hideBalance, monetaryBalance } = useCryptoBalance();

  return (
    <Container $inline={!!inline}>
      <Amount>
        <UserBalanceAmount
          inline={!!inline}
          primaryCurrency={primaryCurrency}
          hideBalance={disableToggle ? false : hideBalance}
          monetaryBalance={monetaryBalance}
        />
      </Amount>
      <Actions>
        {!disableToggle && (
          <Icon
            color="quaternary"
            size="small"
            disableDebounce
            icon={hideBalance ? faEye : faEyeSlash}
            onClick={() => toggleHideBalance()}
            $inline={!!inline}
            disabled={loading}
          />
        )}
        {right}
      </Actions>
    </Container>
  );
};
